import { NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { sendArticleClickEvent } from 'src/helpers/analytics';

import { Space, Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import { Interceptor } from '@sravni/ugc/lib/utils/sentry/Interceptor';

import { SravniNewsSharedEnumsPlacementType } from '../../__generated__/news/Api';
import { GetSpecialProject } from '../../components/GetSpecialProject';
import { HeadingWithFilters } from '../../components/HeadingWithFilters';
import { LeftColumnContent } from '../../components/LeftColumn';
import SeoBlocks from '../../components/SeoBlocks';
import { SpecialProjectsList } from '../../components/SpecialProjectsList';
import { FILTERS, FILTERS_NAMES } from '../../constants/news';
import { makeSpecialProjectsMetadata } from '../../helpers/seo';
import { IRouterQuery, THighLight } from '../../interfaces/news';
import { setMetadata } from '../../reducers/metadata';
import {
  fetchAllDictionariesIfNeeded,
  fetchHighlightsListIfNeeded,
  fetchRubricsListIfNeeded,
  fetchTagsByRubricIfNeeded,
} from '../../reducers/news';
import { useWhitelabel } from '../../reducers/whitelabel';
import styles from '../../styles/common.module.scss';
import { App } from '../../types';
import { updateQueryFilters } from '../../utils/routing';
import { PageWithSubscriptionPopupProps } from '../_app';

// @ts-ignore
const SpecialProjectsPage: NextPage<App.ReduxNextPageContext<IRouterQuery> & PageWithSubscriptionPopupProps> = ({
  subscriptionPopupState,
}) => {
  const { query } = useRouter();
  const isMobile = useIsMobile();
  const isWhitelabel = useWhitelabel();

  const [filters, setFilters] = useState<FILTERS>({});

  useEffect(() => {
    updateQueryFilters(filters, query, 'specialProjects');
    // eslint-disable-next-line
  }, [filters]);

  const updateFilters = (target, value, currentFilters) => {
    const newFilters = {
      ...currentFilters,
      [target]: value,
    };

    setFilters((prevState) => ({
      ...prevState,
      ...newFilters,
      ...(target !== FILTERS_NAMES.TEXT && { text: prevState[FILTERS_NAMES.TEXT] }),
    }));
  };

  const handleFiltersChange = (target, value) => {
    let newValue = value;

    if (target === FILTERS_NAMES.TAG && !Array.isArray(value)) {
      const filterIsArray = Array.isArray(filters[target]);
      const valueIndex = !filterIsArray ? -1 : filters[target].indexOf(value);

      if (valueIndex > -1) {
        newValue = filters[target];
        newValue.splice(valueIndex, 1);
        newValue = [...newValue];
      } else {
        newValue = [
          ...(filterIsArray ? filters[target] : []),
          ...(!filterIsArray || !filters[target].includes(value) ? [value] : []),
        ];
      }
    }

    updateFilters(target, newValue, filters);

    window.scrollTo({ top: 0 });
  };

  const handleItemClick = useCallback((index: number, item: THighLight) => {
    sendArticleClickEvent(item.title, index + 1);
  }, []);

  return (
    <Interceptor componentName="special-projects-component">
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.leftColumn}>
            {!isWhitelabel && <LeftColumnContent subscriptionPopupState={subscriptionPopupState} />}
          </div>
          <div className={styles.wideContentColumn}>
            <Typography.Heading level={2} className={isMobile ? 'h-mb-20 h-mt-20 h-ml-20' : 'h-mb-32'}>
              Спецпроекты
            </Typography.Heading>
            <Space direction={isMobile ? 'vertical' : 'horizontal'} size={20} className={'h-mb-20'}>
              <HeadingWithFilters handleFiltersChange={handleFiltersChange} filters={filters} rubric={'specproekty'} />
              <GetSpecialProject />
            </Space>
            <SpecialProjectsList filters={filters} onItemClick={handleItemClick} />
          </div>
        </div>
        <div className={'h-mt-40'}>
          <SeoBlocks className={styles.content} />
        </div>
      </div>
    </Interceptor>
  );
};

// @ts-ignore
SpecialProjectsPage.getInitialProps = async (ctx: App.ReduxNextPageContext<IRouterQuery>) => {
  const {
    store: { dispatch },
    asPath,
  } = ctx;

  const [pathname] = asPath.split('?');

  await Promise.all([
    dispatch(fetchAllDictionariesIfNeeded()),
    dispatch(fetchTagsByRubricIfNeeded({ rubric: 'specproekty' })),
    dispatch(
      fetchHighlightsListIfNeeded({
        Rubric: 'specproekty',
        Placement: SravniNewsSharedEnumsPlacementType.SpecialProjects,
        IncludeBody: false,
        Limit: 100,
      }),
    ),
    dispatch(fetchRubricsListIfNeeded()),
  ]);

  const metadata = makeSpecialProjectsMetadata();
  dispatch(setMetadata(metadata, pathname));
};

export default SpecialProjectsPage;
